import * as FirebaseAPIs from "@letshang/firebase-services"

import { logAnalyticEvent } from "@firebaseConfig/analyticsUtil"
import { functionsAccessor } from "@firebaseConfig/clientApp"

// Utility to bind functionsAccessor to any Firebase API function
export function wrapWithFunctionsAccessor<
  T extends (functions: any, payload: any) => Promise<any>,
>(
  apiFunction: T,
  functionsAccessor: Parameters<T>[0],
): (payload: Parameters<T>[1]) => Promise<ReturnType<T>> {
  return async (payload: Parameters<T>[1]) => {
    try {
      return (await apiFunction(functionsAccessor, payload)) as ReturnType<T>
    } catch (error) {
      const logEventName = `${apiFunction.name || "unknown_api"}_failure_web`
      const eventParams = {
        api_name: apiFunction.name || "unknown_api",
        error_message: (error as Error)?.message || "Unknown error",
        payload: JSON.stringify({
          ...payload,
          api_name: apiFunction.name || "unknown_api",
          error_message: (error as Error)?.message || "Unknown error",
        }),
      }

      logAnalyticEvent(logEventName, eventParams)

      throw error
    }
  }
}

// Avoid typing FirebaseAPIs directly as 'typeof', which marks everything readonly
type FirebaseApiFunction = (functions: any, payload: any) => Promise<any>
type FirebaseApiModule = Record<string, FirebaseApiFunction>

type WrappedApiFunctions = {
  [K in keyof FirebaseApiModule]: FirebaseApiModule[K] extends (
    functions: any,
    payload: infer P,
  ) => Promise<infer R>
    ? (payload: P) => Promise<R>
    : never
}

const wrappedApi: Partial<WrappedApiFunctions> = {}

Object.entries(FirebaseAPIs).forEach(([key, fn]) => {
  if (typeof fn === "function") {
    wrappedApi[key as keyof FirebaseApiModule] = wrapWithFunctionsAccessor(
      fn as FirebaseApiFunction,
      functionsAccessor,
    )
  }
})

// Destructure only the specific functions you want to use
export const {
  // BuddiesApi
  fetchSuggestedBuddies,
  addBuddiesAPI,
  changePersonStatusAPI,

  // EventsApi
  getEventDetails,
  joinEvent,
  getAllTicketsApi,
  createPaymentSheetApi,
  leaveEventApi,
  leaveNonTicketedEventApi,
  closeDownEvent,
  validateToken,
  markUserAsPermanent,
  getAllEvents,
  searchEvents,
  setPlusOnes,
  getAcceptedGuestAvatarsApi,
  fetchGuestListForEvent,
  fetchMessageRecordForEvent,
  fetchInvitedListForEvent,
  claimTicketForEvent,
  processGroupTicketsAPI,
  applyOrRemoveCoupon,
  increaseInterestedCountApi,
  getEventTemplatesAPI,
  joinWaitlistForEvent,
  createEvent,
  createEventWeb,
  eventGoLive,
  onboardUserToStripe,
  addTicketsApi,
  queryOpenAPIForDescription,
  sendFeedbackForEvent,
  deleteTicketApi,
  updateTicketApi,
  approveApplicant,
  disapproveApplicant,
  scanTicket,

  // GroupsApi
  getGroupDetails,

  // OrganizationApi
  createOrganizationAPI,
  getOrganizationAPI,
  followOrganizationAPI,
  unfollowOrganizationAPI,
  getOrganizationEventsAPI,

  // UserApi
  updateUserDetails,
  getUserInfo,
  updateEmailAPI,
  resendEmailVerificationAPI,
  deleteUserAPI,

  sendExperimentEngagementToSlack,

  requestPluginAPI,
} = wrappedApi as Required<WrappedApiFunctions>
